import React, { useEffect } from 'react';
import { Box, Button, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useLocation, useNavigate, useParams } from 'react-router';
import { SingleColumnPageBase } from 'src/components/PageBase/SingleColumnPageBase';
import { useGetProjectQuery, useGetProjectUsersQuery } from 'src/api/v2/project-v2-ApiSlice';
import { useGetUserInfoQuery } from 'src/api/v2/user-v2-ApiSlice';
import { ProjectUserCards } from 'src/pages/ProjectUsersPage/components/ProjectUserCards';
import { InviteSection } from 'src/pages/ProjectUsersPage/components/InviteSection';
import { hasPermissionForFuncInRole } from 'src/utils/controllerHasPermissionsAdapter';
import { useAppDispatch } from 'src/app/hooks';
import { showSnackBar } from 'src/api/ui-slice';
import { ts } from 'src/utils/translate';
import { theme } from 'src/theme/mui-theme';
import { DOUBLE_SPACING } from 'src/theme/style-consts';

const PAGE_CONTENT_MAX_WIDTH = 1100;
const ROLES_DESCRIPTION_URL_HU = 'https://uplan.io/hu/szerepkorok';
const ROLES_DESCRIPTION_URL = 'https://uplan.io/team-roles-and-permissions';

export const ProjectUsersPage = () => {
    const params = useParams();
    const projectId = Number(params.projectId);
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useAppDispatch();

    const { data: extendedProject } = useGetProjectQuery(projectId, { refetchOnMountOrArgChange: true });
    const { data: users, isError: isUsersError } = useGetProjectUsersQuery(projectId);
    const { data: userInfo } = useGetUserInfoQuery();

    useEffect(() => {
        if (isUsersError) {
            dispatch(showSnackBar({
                text: ts('project_users_load_error', 'Failed to load project users'),
                severity: 'error',
            }));
        }
    }, [isUsersError, dispatch]);

    const handleBack = () => {
        navigate(location.state?.from ?? '/projects');
    };

    const canInvite = extendedProject != null
        && hasPermissionForFuncInRole('users_sect.inv_user', extendedProject.userRole);
    const canEditUsers = extendedProject != null
        && hasPermissionForFuncInRole('users_sect.edit_user', extendedProject.userRole);
    const hasAddress =extendedProject != null
        && !!extendedProject.project.city
        && !!extendedProject.project.zip_code;

    const rolesDescriptionUrl = userInfo?.siteLanguage === 'hu-HU'
        ? ROLES_DESCRIPTION_URL_HU
        : ROLES_DESCRIPTION_URL;

    return (
        <SingleColumnPageBase
            pageContent={
                <Box sx={{ overflowY: 'auto', minHeight: 0 }}>
                    <Stack spacing={DOUBLE_SPACING} sx={{ maxWidth: PAGE_CONTENT_MAX_WIDTH, marginX: 'auto' }}>
                        <Box>
                            <Button startIcon={<ArrowBackIcon />} onClick={handleBack}>
                                {ts('q_sec_gotoback', 'Go back')}
                            </Button>
                        </Box>
                        <Box>
                            <Stack direction="row" alignItems="center">
                                <Typography variant="h5">
                                    {ts('project_users_title', 'Contacts')}
                                </Typography>
                                <Tooltip title={ts('project_users_roles_info', 'Click here to show the descriptions of the roles.')}>
                                    <IconButton size="small" href={rolesDescriptionUrl} target="_blank" rel="noopener noreferrer">
                                        <InfoOutlinedIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Stack>
                            <Typography variant="body2" color={theme.palette.text.secondary}>
                                {extendedProject?.project.project_name}
                            </Typography>
                        </Box>
                        {users && extendedProject && (
                            <ProjectUserCards
                                projectId={projectId}
                                users={users}
                                canEdit={canEditUsers}
                                inviteSlot={canInvite && (
                                    <InviteSection
                                        projectId={projectId}
                                        roles={users.roles}
                                        hasAddress={hasAddress}
                                        readOnly={extendedProject.readOnly}
                                    />
                                )}
                            />
                        )}
                    </Stack>
                </Box>
            }
        />
    );
};
